import { forwardRef, useRef, useState, useEffect, useCallback, ForwardedRef } from "react";
import * as React from "react";
import { Modal } from "react-bootstrap";

import { classNames } from "@/lib/util";
import { translate } from "./IntlProviderWrapper";

import "./Toolbar.scss";

/* -------------------------------------------------------------------------------------------------
 * Toolbar
 * -----------------------------------------------------------------------------------------------*/

type ToolbarProps = {
    children: React.ReactNode;
    className?: string;
    drawerClassName?: string;
};

const Toolbar = forwardRef<HTMLDivElement, ToolbarProps>((props: ToolbarProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { children, className, drawerClassName } = props;
    const [overflow, setOverflow] = useState<boolean>(false);
    const [show_drawer, setShowDrawer] = useState<boolean>(false);
    const container_ref = useRef<HTMLDivElement | null>(null);

    const updateOverflow = useCallback(() => {
        const container = container_ref.current;
        if (container !== null) {
            container.style.overflow = "hidden";
            const client_width = container.clientWidth;
            const scroll_width = container.scrollWidth;
            container.style.overflow = "";
            if (scroll_width > client_width || container.children.length === 0) {
                setOverflow(true);
            } else {
                setOverflow(false);
            }
        }
    }, [container_ref]);

    const updateContainerRef = useCallback(
        (cr: HTMLDivElement | null) => {
            const changed = container_ref.current !== cr;
            container_ref.current = cr;

            if (cr && changed) {
                updateOverflow();
            }
        },
        [updateOverflow]
    );

    const toggleDrawer = useCallback(() => {
        setShowDrawer(!show_drawer);
    }, [show_drawer]);

    useEffect(() => {
        window.addEventListener("resize", updateOverflow);
        updateOverflow();

        return () => {
            window.removeEventListener("resize", updateOverflow);
        };
    }, []);

    // Render toolbar items in modal (drawer) when, content exceeds toolbar client width.
    return (
        <div ref={ref} className={classNames("toolbar", className)} data-overflow={overflow}>
            <div className={classNames("toolbar__container", { "has-overflow": overflow })} ref={updateContainerRef}>
                {!show_drawer && children}
            </div>
            <div className={classNames("toolbar__toggle", { "is-visible": overflow })}>
                <button className="btn btn-outline-secondary" onClick={toggleDrawer}>
                    <i className="fas fa-ellipsis-v" />
                    <span className="sr-only">{translate("common.more", "More")}</span>
                </button>
                <Modal
                    className={classNames("toolbar__drawer", drawerClassName)}
                    dialogClassName="modal-dialog-scrollable"
                    show={show_drawer}
                    onHide={toggleDrawer}
                    animation={false}
                >
                    <Modal.Header>
                        <button type="button" className="close" onClick={toggleDrawer}>
                            <span aria-hidden="true">×</span>
                            <span className="sr-only">{translate("common.close", "Close")}</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="toolbar__drawer-content">{show_drawer && children}</div>
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
});

/* -------------------------------------------------------------------------------------------------
 * ToolbarGroup
 * -----------------------------------------------------------------------------------------------*/

type ToolbarGroupProps = {
    children: React.ReactNode;
    className?: string;
    id?: string;
    grow?: boolean;
    align?: "start" | "center" | "end";
    separator?: "before" | "after";
};

const ToolbarGroup = forwardRef<HTMLDivElement, ToolbarGroupProps>(
    (props: ToolbarGroupProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { children, className, align, grow, separator, id } = props;
        return (
            <div
                id={id}
                ref={ref}
                className={classNames("toolbar-group", className, {
                    "toolbar-group--grow": grow,
                    "toolbar-group--separator-before": separator === "before",
                    "toolbar-group--separator-after": separator === "after",
                    "toolbar-group--align-start": align === "start",
                    "toolbar-group--align-center": align === "center",
                    "toolbar-group--align-end": align === "end",
                })}
            >
                {children}
            </div>
        );
    }
);

/* -------------------------------------------------------------------------------------------------
 * ToolbarItem
 * -----------------------------------------------------------------------------------------------*/

type ToolbarItemProps = {
    children: React.ReactNode;
    className?: string;
    id?: string;
};

const ToolbarItem = forwardRef<HTMLDivElement, ToolbarItemProps>(
    (props: ToolbarItemProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { children, className, id } = props;
        return (
            <div ref={ref} id={id} className={classNames("toolbar-item", className)}>
                {children}
            </div>
        );
    }
);

/* -------------------------------------------------------------------------------------------------
 * ToolbarTitle
 * -----------------------------------------------------------------------------------------------*/

type ToolbarTitleProps = {
    children: React.ReactNode;
    className?: string;
};

const ToolbarTitle = forwardRef<HTMLDivElement, ToolbarTitleProps>(
    (props: ToolbarTitleProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { children, className } = props;
        return (
            <div ref={ref} className={classNames("toolbar-title", className)}>
                {children}
            </div>
        );
    }
);

export { Toolbar, ToolbarGroup, ToolbarItem, ToolbarTitle };

export type { ToolbarProps, ToolbarGroupProps, ToolbarItemProps, ToolbarTitleProps };
